import React, { useEffect, useRef, useState } from 'react';
import { Zap, Clock, CheckCircle2, AlertTriangle, ArrowRight, Camera, Gauge, Wrench, Send } from 'lucide-react';

/**
 * QuickDrawDemo — the real DrawReady score in a hero card. Loops: starts at 42
 * "NOT BANK-READY" with COI + Lien missing, then fixes them and climbs to 100
 * "BANK-READY". Click "Fix My Draw" to trigger instantly. Reflects the actual
 * in-app DrawReady scoring (invoice / COI / lien-waiver requirements).
 */
const START = 42;
const STEPPER = [
  { icon: Camera, label: 'Capture' },
  { icon: Gauge, label: 'Score' },
  { icon: Wrench, label: 'Fix' },
  { icon: Send, label: 'Submit' },
];

export default function DrawScoreDemo() {
  const [fixed, setFixed] = useState(false);
  const [display, setDisplay] = useState(START);
  const [active, setActive] = useState(1); // stepper index
  const loop = useRef(null);

  const target = fixed ? 100 : START;

  useEffect(() => {
    let raf;
    const tick = () => {
      setDisplay((d) => {
        if (Math.abs(d - target) < 0.6) return target;
        raf = requestAnimationFrame(tick);
        return d + (target - d) * 0.14;
      });
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  // Auto loop: fix after a beat, then reset.
  useEffect(() => {
    const schedule = () => {
      loop.current = setTimeout(() => { setFixed(true); setActive(3); }, 2600);
      loop.current = setTimeout(() => { setActive(4); }, 4200);
      const reset = setTimeout(() => { setFixed(false); setActive(1); schedule(); }, 7000);
      return reset;
    };
    const r = schedule();
    return () => { clearTimeout(loop.current); clearTimeout(r); };
  }, []);

  const doFix = () => { setFixed(true); setActive(3); };

  const shown = Math.round(display);
  const bankReady = shown >= 100;
  const color = bankReady ? '#059669' : '#f59e0b';
  const R = 46, C = 2 * Math.PI * R;
  const offset = C * (1 - shown / 100);

  const items = [
    { key: 'invoice', label: 'Invoice', ok: true },
    { key: 'coi', label: 'COI', ok: fixed },
    { key: 'lien', label: 'Lien Waiver', ok: fixed },
  ];

  return (
    <div className="rounded-2xl bg-white shadow-2xl border border-slate-200 p-5 sm:p-6 w-full max-w-md mx-auto" data-testid="draw-score-demo">
      <div className="flex items-center justify-between mb-4">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600"><Zap className="w-4 h-4" /> QUICK DRAW DEMO</span>
        <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Clock className="w-3.5 h-3.5" /> 00:18</span>
      </div>

      <div className="flex gap-4">
        {/* Gauge */}
        <div className="flex flex-col items-center shrink-0">
          <p className="text-[11px] font-semibold text-slate-500 mb-1">Draw Score</p>
          <div className="relative">
            <svg width="118" height="118" viewBox="0 0 110 110" className="-rotate-90">
              <circle cx="55" cy="55" r={R} fill="none" stroke="#e2e8f0" strokeWidth="9" />
              <circle cx="55" cy="55" r={R} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={offset} style={{ transition: 'stroke .4s' }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-extrabold" style={{ color }}>{shown}</span>
              <span className="text-[9px] text-slate-400">/100</span>
            </div>
          </div>
          <span className={`mt-1 text-[10px] font-bold ${bankReady ? 'text-emerald-600' : 'text-amber-600'}`}>{bankReady ? 'BANK-READY' : 'NOT BANK-READY'}</span>
        </div>

        {/* Fix list */}
        <div className="flex-1">
          <p className="text-[11px] font-semibold text-slate-500 mb-2">Fix what's missing</p>
          <div className="space-y-1.5">
            {items.map((it) => (
              <div key={it.key} className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm transition-all ${it.ok ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                {it.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                <span className="font-medium text-slate-700">{it.label}</span>
                <span className={`ml-auto text-[11px] font-semibold ${it.ok ? 'text-emerald-600' : 'text-amber-600'}`}>{it.ok ? 'Attached' : 'Missing'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Prompt + CTA */}
      <div className={`mt-4 rounded-xl px-3 py-2.5 flex items-center gap-3 ${bankReady ? 'bg-emerald-50' : 'bg-slate-50'}`}>
        <div className="flex-1">
          <p className="text-xs font-semibold text-slate-700">{bankReady ? 'All set — ready to submit' : 'Score low?'}</p>
          <p className="text-[11px] text-slate-500">{bankReady ? 'Your package is bank-ready.' : 'Fix the items above to reach 100.'}</p>
        </div>
        {!bankReady && (
          <button onClick={doFix} data-testid="demo-fix-btn" className="inline-flex items-center gap-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-2 transition-colors">
            Fix My Draw <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Stepper */}
      <div className="mt-5 flex items-center justify-between">
        {STEPPER.map((s, i) => {
          const on = i + 1 <= active;
          return (
            <React.Fragment key={s.label}>
              <div className="flex flex-col items-center gap-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center ${on ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                  <s.icon className="w-3.5 h-3.5" />
                </div>
                <span className={`text-[10px] ${on ? 'text-slate-700 font-semibold' : 'text-slate-400'}`}>{s.label}</span>
              </div>
              {i < STEPPER.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i + 1 < active ? 'bg-blue-600' : 'bg-slate-200'}`} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
